<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { usePlanStore } from '@/stores/plan';
import { useSearchStore } from '@/stores/search';
import { onClickOutside } from '@vueuse/core';
import ThemeToggle from '../ThemeToggle.vue';
import SearchModal from '@/components/search/SearchModal.vue';
import CompanySelector from '../company/CompanySelector.vue';
import LanguageSelector from '@/components/language/LanguageSelector.vue';
import NotificationBell from '@/components/notifications/NotificationBell.vue';
import { Bars3Icon, MagnifyingGlassIcon, ArrowLeftOnRectangleIcon, XMarkIcon, CreditCardIcon, BuildingOfficeIcon } from '@heroicons/vue/24/outline';
import { useShipmentStats } from '@/composables/useShipmentStats';
import CompanyShareModal from '../companies/CompanyShareModal.vue';
import { checkClientType } from '@/utils/checkClientType';

const emit = defineEmits(['toggleSidebar', 'companyChanged', 'themeToggle']);

const props = defineProps<{
  isSidebarCollapsed: boolean
}>()

const router = useRouter();
const authStore = useAuthStore();
const planStore = usePlanStore();
const searchStore = useSearchStore();
const companyStore = useCompanyStore();
const { resetStats } = useShipmentStats();
const { t } = useI18n();

// Company selector
const selectedCompanyId = computed({
  get: () => companyStore.selectedCompanyId,
  set: (value) => {
    if (value) {
      companyStore.setSelectedCompany(value);
      emit('companyChanged', value);
    }
  }
});

const companies = computed(() => {
  const result = companyStore.availableCompanies || [];
  return result;
});
const currentCompany = computed(() => companyStore.getSelectedCompany());

// Search
const searchQuery = ref('');
const searchContainer = ref(null);
const isSearching = ref(false);

const openSearch = () => {
  searchStore.openSearchModal();
  isSearching.value = true;
};

const closeSearch = () => {
  searchStore.closeSearchModal();
  isSearching.value = false;
};

watch(() => searchStore.isSearchModalOpen, (isOpen) => {
  if (!isOpen) {
    isSearching.value = false;
  }
});

// Notifications
const showNotifications = ref(false);
const notificationsRef = ref(null);

// User Menu
const showUserMenu = ref(false);
const userMenuRef = ref(null);

// User info computeds
const userDisplayName = computed(() => {
  const user = authStore.user;
  if (!user?.name || !user?.lastName) return '';
  return `${user.name} ${user.lastName}`;
});

const userEmail = computed(() => authStore.user?.email || '');

const userInitials = computed(() => {
  const user = authStore.user;
  if (!user?.name || !user?.lastName) return '';
  
  const firstInitial = user.name.charAt(0) || '';
  const lastInitial = user.lastName.charAt(0) || '';
  
  return (firstInitial + lastInitial).toUpperCase();
});

// Navigation functions
const navigateToProfile = () => {
  router.push('/profile');
  showUserMenu.value = false;
};

const navigateToSettings = () => {
  router.push('/settings');
  showUserMenu.value = false;
};

const navigateToBilling = () => {
  router.push('/dashboard/billing');
  showUserMenu.value = false;
};

const handleLogout = async () => {
  resetStats(); // Limpiar stats antes de cerrar sesión
  await authStore.logout();
  router.push('/login');
};

const handleCompanyChange = (companyId: string) => {
  companyStore.setSelectedCompany(companyId);
  emit('companyChanged', companyId);
};

// Close dropdowns when clicking outside
onClickOutside(searchContainer, () => {
  if (!searchStore.isSearchModalOpen) {
    searchStore.resetSearch();
  }
});

onClickOutside(notificationsRef, () => {
  showNotifications.value = false;
});

onClickOutside(userMenuRef, () => {
  showUserMenu.value = false;
});

// Sample notifications
const notifications = [
  {
    id: 1,
    title: 'Nuevo embarque creado',
    message: 'El embarque #12345 ha sido creado exitosamente',
    time: '5 min',
    type: 'info'
  },
  {
    id: 2,
    title: 'Alerta de retraso',
    message: 'Posible retraso en embarque #12340',
    time: '1 hora',
    type: 'warning'
  },
  {
    id: 3,
    title: 'Actualización de sistema',
    message: 'Nueva versión disponible',
    time: '2 horas',
    type: 'system'
  }
];

// Add keyboard shortcut for search
onMounted(() => {
  const handleKeydown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      openSearch();
    }
  };
  
  window.addEventListener('keydown', handleKeydown);
  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
  });
});

// Modal de compartir
const showShareModal = ref(false);

const openShareModal = () => {
  showShareModal.value = true;
};
</script>

<template>
  <header class="bg-white dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700 sticky top-0 z-[45]">
    <div class="flex h-16 items-center justify-between px-4">
      <!-- Mobile menu button -->
      <button 
        @click="$emit('toggleSidebar')"
        class="lg:hidden p-2 rounded-lg text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
      >
        <Bars3Icon class="h-6 w-6" />
      </button>

      <!-- Company Selector -->
      <div class="flex items-center lg:ml-4">
        <div v-if="companies.length > 0" class="relative">
          <CompanySelector @share="showShareModal = true" />
        </div>
        <div v-else-if="companyStore.isLoadingAllCompanies" class="flex items-center text-secondary-700 dark:text-secondary-300">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2"></div>
          <span class="text-sm font-medium">{{ t('dashboard.header.companySelector.loading') }}</span>
        </div>
        <div v-else-if="currentCompany" class="flex items-center text-secondary-700 dark:text-secondary-300">
          <BuildingOfficeIcon class="h-5 w-5 mr-2" />
          <span class="text-sm font-medium truncate max-w-[200px]">
            {{ currentCompany === null ? t('dashboard.header.companySelector.allCompanies') : currentCompany.name }}
          </span>
        </div>
      </div>

      <!-- Search -->
      <div class="flex-1 px-4 max-w-xl relative" ref="searchContainer">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon 
              class="h-5 w-5"
              :class="[
                searchStore.isSearching || isSearching 
                  ? 'text-primary-500 animate-pulse' 
                  : 'text-secondary-400'
              ]"
            />
          </div>
          <input
            v-model="searchQuery"
            type="search"
            :placeholder="t('dashboard.header.searchPlaceholder')"
            class="w-full pl-10 pr-10 rounded-lg border-secondary-300 dark:border-secondary-700 dark:bg-secondary-900 focus:ring-primary-500 focus:border-primary-500"
            @click="openSearch"
            readonly
          />
          <button 
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <XMarkIcon class="h-5 w-5 text-secondary-400 hover:text-secondary-500" />
          </button>
        </div>
      </div>

      <!-- Right section -->
      <div class="flex items-center gap-2">
        <!-- Actions slot -->
        <slot name="actions"></slot>

        <!-- Notificaciones de correo (KILA-362) -->
        <NotificationBell />

        <!-- Language Selector -->
        <LanguageSelector />
        
        <!-- Theme Toggle -->
        <ThemeToggle @theme-toggle="$emit('themeToggle')" />

        <!-- Search Button -->
        
        <!-- Notifications -->

          <!-- Notifications Dropdown -->
                 <!-- User Menu -->
        <div v-if="authStore.user" class="relative" ref="userMenuRef">
          <button
            @click="showUserMenu = !showUserMenu"
            class="flex items-center gap-2 p-2 rounded-lg text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
          >
            <div
              class="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center text-primary-600 dark:text-primary-200 font-semibold"
            >
              {{ userInitials }}
            </div>
            <span class="hidden md:block text-sm font-medium text-secondary-700 dark:text-secondary-200">
              {{ userDisplayName }}
            </span>
          </button>

          <!-- User Dropdown -->
          <div
            v-if="showUserMenu"
            class="absolute right-0 mt-2 w-64 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-secondary-200 dark:border-secondary-700 py-1"
          >
            <!-- User info -->
            <div class="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
              <p class="text-sm font-medium text-secondary-900 dark:text-white">
                {{ userDisplayName }}
              </p>
              <p class="text-sm text-secondary-500 dark:text-secondary-400 truncate">
                {{ userEmail }}
              </p>
            </div>

            <!-- Plan info -->

            <!-- Menu items -->
            <div class="py-1">

              <button
                @click="navigateToBilling"
                v-if="checkClientType(authStore.user?.clientType)"
                class="w-full px-4 py-2 text-left text-sm text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700 flex items-center gap-2"
              >
                <CreditCardIcon class="w-5 h-5" />
                {{ t('dashboard.header.userMenu.billing') }}
              </button>
            </div>

            <!-- Logout -->
            <div class="border-t border-secondary-200 dark:border-secondary-700 py-1">
              <button
                @click="handleLogout"
                class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
              >
                <ArrowLeftOnRectangleIcon class="w-5 h-5" />
                {{ t('dashboard.header.userMenu.logout') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
  <SearchModal />
  <CompanyShareModal
    v-if="showShareModal"
    :company-id="selectedCompanyId!"
    :is-open="showShareModal"
    @close="showShareModal = false"
  />
</template>